import { ListCell } from '@h2oai/ui-kit';
import React from 'react';

import { defaultAppstoreWorkspaceName, defaultGlobalWorkspaceName } from '../../../../authz/authz.hooks';
import { RoleBinding } from '../../../../authz/gen/ai/h2o/authorization/v1/role_binding_pb';
import { useUsers } from '../../../../authz/providers/UsersProvider';
import { useQueryAllRoleBindings } from '../../hooks/useQueryAllRoleBindings';
import { groupRoleBindingsToGroupsAndUsers, isOwnerRoleBinding } from '../../utils/workspaceUtils';
import { WorkspaceSharedWithCellPure } from '../Parts/Parts';

export const Owner = React.memo(function Creator(props: { workspaceName: string }) {
  const roleBindings = useQueryAllRoleBindings(props.workspaceName);
  const workspaceOwnerBindings = React.useMemo(() => {
    return roleBindings?.filter(isOwnerRoleBinding);
  }, [roleBindings]);
  const { usersAndGroupsNameMap } = useUsers();
  if (props.workspaceName === defaultAppstoreWorkspaceName || props.workspaceName === defaultGlobalWorkspaceName) {
    return <ListCell headerText="Owner" text="N/A" />;
  }
  if (!workspaceOwnerBindings?.length) {
    return <ListCell headerText="Owner" text="Information restricted" />;
  }
  if (workspaceOwnerBindings.length < 2) {
    const ownerDisplayName = usersAndGroupsNameMap?.[workspaceOwnerBindings[0].subject!]?.displayName;
    return <ListCell headerText="Owner" text={ownerDisplayName || `Information restricted`} />;
  }
  const { groups, users } = groupRoleBindingsToGroupsAndUsers(workspaceOwnerBindings);
  return (
    <WorkspaceSharedWithCellPure
      groupsDisplayNames={(groups || []).map(
        (g: RoleBinding) => usersAndGroupsNameMap?.[g.subject!]?.displayName || `unknown group`
      )}
      header="Owners"
      usersDisplayNames={(users || []).map(
        (u: RoleBinding) => usersAndGroupsNameMap?.[u.subject!]?.displayName || `unknown user`
      )}
    />
  );
});

export const SharedWith = React.memo(function SharedWith(props: { workspaceName: string }) {
  const allRoleBindings = useQueryAllRoleBindings(props.workspaceName);
  const { usersAndGroupsNameMap } = useUsers();

  const roleBindings = React.useMemo(() => {
    if (!allRoleBindings?.length) return [];

    const uniqueSubjects = new Set<string>();
    const uniqueRoleBindings: RoleBinding[] = [];

    for (const roleBinding of allRoleBindings) {
      const subject = roleBinding.subject || '';
      if (subject && !uniqueSubjects.has(subject)) {
        uniqueSubjects.add(subject);
        uniqueRoleBindings.push(roleBinding);
      }
    }

    return uniqueRoleBindings;
  }, [allRoleBindings]);

  if (props.workspaceName === defaultAppstoreWorkspaceName || props.workspaceName === defaultGlobalWorkspaceName) {
    return <ListCell headerText="Shared with" text="N/A" />;
  }
  if (!roleBindings?.length) {
    return <ListCell headerText="Shared with" text="Information restricted" />;
  }
  if (roleBindings.length < 2) {
    const userDisplayName = usersAndGroupsNameMap?.[roleBindings[0].subject!]?.displayName;
    return <ListCell headerText="Shared with" text={userDisplayName || `Information restricted`} />;
  }
  const { groups, users } = groupRoleBindingsToGroupsAndUsers(roleBindings);
  return (
    <WorkspaceSharedWithCellPure
      groupsDisplayNames={(groups || []).map(
        (g: RoleBinding) => usersAndGroupsNameMap?.[g.subject!]?.displayName || `unknown group`
      )}
      header={`Shared with (${roleBindings.length})`}
      usersDisplayNames={(users || []).map(
        (u: RoleBinding) => usersAndGroupsNameMap?.[u.subject!]?.displayName || `unknown user`
      )}
    />
  );
});
